/**
 * Does an Account carry a usable map position (BillingLatitude / BillingLongitude)?
 *
 * Owner 2026-10-27: a case is not opened on an Account without coordinates — the
 * same block as a missing זיהוי מחמיר (the strict-identification modal only saves
 * with coordinates since 2026-06-10). 22 Accounts were marked strict-identified
 * before that and still have none; they are treated as not identified.
 *
 * Coordinates arrive as numbers or TEXT ('31.66', '', '0').
 */
export function hasAccountCoords(account) {
  const lat = parseFloat(account?.BillingLatitude);
  const lng = parseFloat(account?.BillingLongitude);
  return Number.isFinite(lat) && Number.isFinite(lng) && lat !== 0 && lng !== 0;
}

/** Strict identification counts only with coordinates. */
export function isStrictIdentified(account) {
  const v = account?.Strict_Identification__c;
  const done = v === true || v === 1 || ['true', '1', 't'].includes(String(v ?? '').toLowerCase());
  return done && hasAccountCoords(account);
}

// Owner 2026-10-27: the same block wherever a case or an opportunity is opened by hand.
//   case        → strict identification (which needs coordinates)
//   opportunity → coordinates only
// Email-to-case is not gated.
export const GATE_MESSAGES = {
  noStrictId: 'לא בוצע זיהוי מחמיר בחשבון. יש להיכנס לכרטיס החשבון וללחוץ על "זיהוי מחמיר" לאימות הכתובת עם הלקוח.',
  noCoords: 'לחשבון אין מיקום במפה (קואורדינטות). יש להיכנס לכרטיס החשבון, ללחוץ על "זיהוי מחמיר" ולבחור את הכתובת מתוך החיפוש.',
  noAccount: 'לא נבחר חשבון.',
  failed: 'לא ניתן לבדוק את החשבון כרגע — נסה שוב.',
};

/** The reason an Account blocks, or null. Pure — for an Account already in hand. */
export function accountGateReason(account, { requireStrictId }) {
  if (!account) return 'noAccount';
  if (requireStrictId && !isStrictIdentified(account)) {
    // Not identified at all → the identification line; identified without a position → the map line.
    const v = account?.Strict_Identification__c;
    const done = v === true || v === 1 || ['true', '1', 't'].includes(String(v ?? '').toLowerCase());
    return done ? 'noCoords' : 'noStrictId';
  }
  if (!hasAccountCoords(account)) return 'noCoords';
  return null;
}

/**
 * Re-read the Account (a same-session edit must count) and check it.
 * Fails closed, like the case modal: an Account that cannot be read blocks.
 * → { ok, reason, message }
 */
export async function checkAccountGate(apiBase, accountId, { requireStrictId, fetchImpl } = {}) {
  const doFetch = fetchImpl || fetch;
  if (!accountId) return { ok: false, reason: 'noAccount', message: GATE_MESSAGES.noAccount };
  try {
    const r = await doFetch(`${apiBase}/api/objects/Account/records/${accountId}`);
    if (!r.ok) return { ok: false, reason: 'failed', message: GATE_MESSAGES.failed };
    const j = await r.json();
    const reason = accountGateReason(j.record || j, { requireStrictId: !!requireStrictId });
    return reason ? { ok: false, reason, message: GATE_MESSAGES[reason] } : { ok: true, reason: null, message: '' };
  } catch (e) {
    return { ok: false, reason: 'failed', message: GATE_MESSAGES.failed };
  }
}
